import { useContext, useEffect } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { ArchiveFinalExaminationPageUI } from './ui'
import { IArchiveFinalExamination } from './types'

import { useFinalExamination } from '@api/finalExamination/state'

import { useValue } from '@hooks/useValue'
import { useDebounce } from '@hooks/useDebounce'

import { IModalConfirm, ModalConfirm } from '@primitive/modal.confirm'

import { CardContractContainer, ICardContractContainer } from '@smart/cardContract'
import { CardListenerContainer, ICardListenerContainer } from '@smart/cardListener'

import { NotificationContext, notifyOpen } from '@ui/notification/notification.provider'


/** Контейнер - Страница архива итоговых работ */
export const ArchiveFinalExaminationContainer = () => {
    const notify = useContext(NotificationContext)

    const cardContract = useValue<ICardContractContainer>({isOpen: false})
    const cardListener = useValue<ICardListenerContainer>({isOpen: false})

    const loadingTitle = useValue<string>('Загрузка архива...')
    const modalConfirm = useValue<IModalConfirm>({isOpen: false})

    const currentPageNumber = useValue<number>(1)
    const txtSearch = useValue<string>('')

    const finalExaminationState = useFinalExamination(useShallow((state) => state))

    useEffect(() => {
        return () => {
            finalExaminationState.clearFinalExaminationArchive()

            currentPageNumber.setValue(1)
            txtSearch.setValue('')
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (finalExaminationState.error) return notify && notifyOpen(finalExaminationState.error.message, finalExaminationState.error.type, 3000, notify)
    }, [finalExaminationState.error]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        loadDataListeners(txtSearch.value)
    }, [currentPageNumber.value]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        finalExaminationState.isReloadArchive && loadDataListeners(txtSearch.value)
    }, [finalExaminationState.isReloadArchive]) // eslint-disable-line react-hooks/exhaustive-deps

    const loadDataListeners = (search?: string) => {
        finalExaminationState.actionGetFinalExaminationArchive(currentPageNumber.value, search || '')
    }

    const handlerOpenModalConfirm = (isOpen: boolean, headerText: string, contentText: string, actionName?: string, recordID?: number) => {
        modalConfirm.setValue({
            isOpen, headerText, contentText,
            handleButtonCancel() { modalConfirm.setValue({isOpen: false}) },
            handleButtonOk() {
                loadingTitle.setValue('Возвращаю на обработку...')

                finalExaminationState.actionSetFinalExaminationWithArchive(Number(recordID)).then((res) => {
                    if (res === 200) {
                        loadDataListeners(txtSearch.value)
                        return notify && notifyOpen('Анкета итоговой работы слушателя успешно возвращена на обработку.', 'success', 1500, notify)
                    }
                }).finally(() => {
                    loadingTitle.setValue('Загрузка архива...')
                })

                modalConfirm.setValue({isOpen: false})
            }
        })
    }

    const handleOpenCardContract = (isOpen: boolean, contractID?: number) => {
        cardContract.setValue({
            isOpen, cardMode: 'edit', cardID: contractID,
            handlerButtonCancel() { cardContract.setValue({isOpen: false}) },
            handlerButtonOk() {
                cardContract.setValue({isOpen: false})
                loadDataListeners(txtSearch.value)
            }
        })
    }

    const handleOpenCardListener = (isOpen: boolean, listenerID?: number) => {
        cardListener.setValue({
            isOpen, cardMode: 'edit', cardID: listenerID,
            handlerButtonCancel() { cardListener.setValue({isOpen: false}) },
            handlerButtonOk() {
                cardListener.setValue({isOpen: false})
                loadDataListeners(txtSearch.value)
            }
        })
    }

    const debounceSearch = useDebounce(loadDataListeners, 1500)
    const txtSearchOnChange = (value: string) => { txtSearch.setValue(value); debounceSearch(value) }

    const propsToComponent: IArchiveFinalExamination = {
        isLoading: finalExaminationState.archive.isLoading,
        txtSearch,
        txtSearchOnChange,
        loadDataListeners,
        loadingTitle,
        currentPageNumber,
        totalCount: finalExaminationState.archive.totalCount,
        pageSize: finalExaminationState.archive.pageSize,
        listenersList: finalExaminationState.archive.items,
        handleOpenCardListener,
        handleOpenCardContract,
        handlerOpenModalConfirm,
    }

    return <>
        <ArchiveFinalExaminationPageUI {...propsToComponent} />

        <ModalConfirm {...modalConfirm.value} />
        <CardListenerContainer {...cardListener.value} />
        <CardContractContainer {...cardContract.value} />
    </>
}